import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { ApiError } from '../api/client'
import { askAdamPlan, donePlanStep, launchPlanStep, planStepToChange, ticketPlanQuery } from '../api/desk'

/** Pasii care pot deveni ciorna de schimbare (`Requests::fromPlanStep`). */
const CHANGE_KINDS = ['runbook', 'catalog_type', 'command_suggested']

export function TicketPlanPanel({ id }: { id: number }) {
  const ticketId = id
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({ ...ticketPlanQuery(ticketId), enabled: ticketId > 0, refetchInterval: 15000 })
  const [armed, setArmed] = useState<number | null>(null)
  const [armedChange, setArmedChange] = useState<number | null>(null)
  const [changeMsg, setChangeMsg] = useState<Record<number, { text: string; error: boolean }>>({})
  const [handNote, setHandNote] = useState<Record<number, string>>({})
  const [handError, setHandError] = useState<Record<number, string>>({})
  const launch = useMutation({
    mutationFn: ({ stepId, body }: { stepId: number; body: Record<string, unknown> }) => launchPlanStep(ticketId, stepId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['easeedesk', 'ticket', ticketId] }),
  })
  const toChange = useMutation({
    mutationFn: (stepId: number) => planStepToChange(ticketId, stepId),
    onSuccess: (out, stepId) => {
      setChangeMsg((m) => ({ ...m, [stepId]: { text: out.message, error: false } }))
      void qc.invalidateQueries({ queryKey: ['easeedesk', 'ticket', ticketId] })
    },
    onError: (e, stepId) =>
      setChangeMsg((m) => ({ ...m, [stepId]: { text: e instanceof ApiError ? e.message : 'The request failed.', error: true } })),
  })

  const byHand = useMutation({
    mutationFn: ({ stepId, note }: { stepId: number; note: string }) => donePlanStep(ticketId, stepId, note),
    onSuccess: (_out, { stepId }) => {
      setHandError((m) => ({ ...m, [stepId]: '' }))
      void qc.invalidateQueries({ queryKey: ['easeedesk', 'ticket', ticketId] })
    },
    onError: (e, { stepId }) =>
      setHandError((m) => ({ ...m, [stepId]: e instanceof ApiError ? e.message : 'The request failed.' })),
  })

  if (isLoading || !data) return <p className="text-muted">Loading plan…</p>
  const onParent = data.on_parent as { ticket_id: number; plan_id: number } | undefined
  if (onParent) {
    return <p>The plan for this incident is on ticket #{onParent.ticket_id}.</p>
  }
  const plan = data.plan as {
    title?: string
    version?: number
    state?: string
    shadow?: boolean
    steps?: Array<Record<string, unknown>>
    ask_adam_at?: string | null
  } | null
  if (!plan) return <p className="text-muted">No remediation plan on this ticket.</p>
  const canLaunch = Boolean(data.can_launch)
  const canFull = Boolean(data.can_full)
  const open = plan.state !== 'superseded' && plan.state !== 'abandoned'

  return (
    <div className="ticket-plan-panel">
      <h4>
        {plan.title} <small className="text-muted">({plan.version ? `version ${plan.version}, ` : ''}{plan.state})</small>
        {plan.shadow && (
          <>
            {' '}
            <span className="label label-warning" title="Shadow mode: nothing was sent to the person">
              SHADOW
            </span>
          </>
        )}
      </h4>
      <ol className="list-unstyled">
        {(plan.steps ?? []).map((step) => {
          const stepId = Number(step.id ?? step.ID ?? 0)
          const kind = String(step.kind ?? '')
          const stepState = String(step.step_state ?? '')
          const launchedBy = String(step.launched_by_name ?? '')
          const resultNote = String(step.result_note ?? '')
          const note = handNote[stepId] ?? ''
          return (
            <li key={stepId} className="panel panel-default" style={{ marginBottom: 12, padding: 12 }}>
              <strong>{String(step.title ?? '')}</strong> <span className="label label-default">{kind}</span>{' '}
              {stepState !== '' && <span className="label label-info">{stepState}</span>}
              <div className="text-muted">{String(step.why ?? '')}</div>
              {launchedBy !== '' && <div className="text-muted">launched by {launchedBy}</div>}
              {resultNote !== '' && <div style={{ marginTop: 6 }}>{resultNote}</div>}
              {kind === 'command_suggested' && canFull && <pre>{String(step.command_text ?? '')}</pre>}
              {kind === 'command_suggested' && !canFull && <p className="text-muted">[shown only with Full Access]</p>}
              {canLaunch && kind !== 'manual' && kind !== 'verify' && open && (
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => {
                    if (armed !== stepId) {
                      setArmed(stepId)
                      return
                    }
                    launch.mutate({ stepId, body: { confirm: true, ...(kind === 'command_suggested' ? { agree: 'I read it' } : {}) } })
                    setArmed(null)
                  }}
                >
                  {armed === stepId ? 'Sure? run now' : 'Run this step'}
                </button>
              )}{' '}
              {canLaunch &&
                CHANGE_KINDS.includes(kind) &&
                String(step.step_state ?? '') === 'proposed' &&
                Number(step.draft_id ?? 0) === 0 &&
                open && (
                  <button
                    type="button"
                    className="btn btn-default btn-sm od-plan-to-change"
                    disabled={toChange.isPending}
                    title="Opens a change proposal for the step's machines, tied to this ticket"
                    onClick={() => {
                      if (armedChange !== stepId) {
                        setArmedChange(stepId)
                        return
                      }
                      toChange.mutate(stepId)
                      setArmedChange(null)
                    }}
                  >
                    {armedChange === stepId ? 'Sure? open the change' : 'Turn into a change proposal'}
                  </button>
                )}
              {canLaunch && kind === 'manual' && open && (stepState === 'proposed' || stepState === 'launched') && (
                <div style={{ marginTop: 6 }}>
                  <textarea
                    className="form-control"
                    rows={2}
                    maxLength={500}
                    aria-label={`What was done on ${String(step.title ?? '')}`}
                    placeholder="Paste what you did and what you saw"
                    value={note}
                    onChange={(e) => setHandNote((m) => ({ ...m, [stepId]: e.target.value }))}
                  />
                  <button
                    type="button"
                    className="btn btn-default btn-sm"
                    style={{ marginTop: 6 }}
                    disabled={note.trim() === '' || byHand.isPending}
                    onClick={() => byHand.mutate({ stepId, note: note.trim() })}
                  >
                    Done by hand
                  </button>
                  {handError[stepId] && <div className="text-danger">{handError[stepId]}</div>}
                </div>
              )}
              {Number(step.draft_id ?? 0) > 0 && (
                <div className="text-muted" style={{ marginTop: 6 }}>
                  Change proposal <a href="/app/change">#{Number(step.draft_id)}</a>
                </div>
              )}
              {changeMsg[stepId] && (
                <div className={changeMsg[stepId].error ? 'text-danger' : 'text-success'} style={{ marginTop: 6 }}>
                  {changeMsg[stepId].text}
                </div>
              )}
            </li>
          )
        })}
      </ol>
      {plan.ask_adam_at && <p className="text-muted">Adam was asked to continue - asked at {plan.ask_adam_at}.</p>}
      {canLaunch && !plan.ask_adam_at && (
        <button type="button" className="btn btn-default" onClick={() => askAdamPlan(ticketId).then(() => qc.invalidateQueries({ queryKey: ['easeedesk', 'ticket', ticketId] }))}>
          Ask Adam to continue
        </button>
      )}
      <PlanHistory history={(data.history as PlanVersion[] | undefined) ?? []} canFull={canFull} />
    </div>
  )
}

type PlanVersion = {
  id: number
  version: number
  title?: string
  summary?: string
  state?: string
  created_at?: string
  ended_at?: string | null
  superseded_by?: { id: number; version: number | null } | null
  abandon_reason?: string
  steps?: Array<Record<string, unknown>>
}

/** Versiunile de dinainte: numai de citit, pasii stransi pana se cer. */
function PlanHistory({ history, canFull }: { history: PlanVersion[]; canFull: boolean }) {
  const [open, setOpen] = useState<Record<number, boolean>>({})
  if (history.length === 0) return null

  return (
    <div className="ticket-plan-history" style={{ marginTop: 18 }}>
      <h5>Earlier versions</h5>
      <ul className="list-unstyled" aria-label="Earlier versions">
        {history.map((v) => {
          const steps = v.steps ?? []
          const expanded = Boolean(open[v.id])
          const label = `${steps.length} ${steps.length === 1 ? 'step' : 'steps'}`
          return (
            <li key={v.id} className="panel panel-default" style={{ marginBottom: 8, padding: 10 }}>
              <div>
                <strong>Version {v.version}</strong> {v.title} <small className="text-muted">({v.state})</small>
              </div>
              <div className="text-muted">
                proposed {v.created_at}
                {endedLine(v) !== '' && <> · {endedLine(v)}</>}
              </div>
              {v.summary && <div>{v.summary}</div>}
              {steps.length > 0 && (
                <button
                  type="button"
                  className="btn btn-link btn-xs"
                  style={{ paddingLeft: 0 }}
                  aria-expanded={expanded}
                  aria-label={`${expanded ? 'Hide' : 'Show'} ${label} of version ${v.version}`}
                  onClick={() => setOpen((m) => ({ ...m, [v.id]: !expanded }))}
                >
                  {expanded ? 'Hide steps' : `Show ${label}`}
                </button>
              )}
              {expanded && (
                <ol>
                  {steps.map((step) => {
                    const launchedBy = String(step.launched_by_name ?? '')
                    const resultNote = String(step.result_note ?? '')
                    return (
                      <li key={Number(step.id ?? step.ID ?? 0)}>
                        {String(step.title ?? '')} <span className="label label-default">{String(step.kind ?? '')}</span>{' '}
                        {String(step.step_state ?? '') !== '' && <span className="label label-info">{String(step.step_state)}</span>}
                        {launchedBy !== '' && <span className="text-muted"> · launched by {launchedBy}</span>}
                        {resultNote !== '' && <div>{resultNote}</div>}
                        {String(step.kind ?? '') === 'command_suggested' && canFull && <pre>{String(step.command_text ?? '')}</pre>}
                      </li>
                    )
                  })}
                </ol>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function endedLine(v: PlanVersion): string {
  const when = v.ended_at ? ` on ${v.ended_at}` : ''
  if (v.state === 'superseded') {
    return v.superseded_by?.version ? `Replaced by Adam with version ${v.superseded_by.version}${when}` : `Replaced by Adam${when}`
  }
  if (v.state === 'abandoned') {
    return `Abandoned${when}${v.abandon_reason ? `: ${v.abandon_reason}` : ''}`
  }
  if (v.state === 'done') {
    return `Done${when}`
  }
  return ''
}
